import { useEffect, useMemo, useState } from "react"
import { getShops } from "../../api/shopsApi"
import { ShopDto } from "../../models/api/shopApiTypes"
import ShopCard from "../common/ShopCard"
import { TownDto } from "../../models/api/districtsApiTypes"
import { CategoryDto } from "../../models/api/categoriesApiTypes"
import { getDistricts } from "../../api/districtsApi"
import { getCategories } from "../../api/categoriesApi"
import ShopsFilter from "./ShopsFilter"
import { useFilters } from "../../context/FilterContext"

// Lowercase and strip accents so that e.g. "caffe" matches "Caffè"
const normalizeText = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

export default function ShopsList() {
  const SHOPS_PER_PAGE = 12
  // Shops are few, so they are all loaded at once and searched/paginated client side
  const ALL_SHOPS_PAGE_SIZE = 1000
  const [loading, setLoading] = useState(true)
  const [shops, setShops] = useState<ShopDto[]>([])
  const [towns, setTowns] = useState<TownDto[]>([])
  const [categories, setCategories] = useState<CategoryDto[]>([])
  const { filters, setFilters } = useFilters()

  const searchText = normalizeText(filters.search)
  const filteredShops = useMemo(
    () => (searchText ? shops.filter((shop) => normalizeText(shop.name).includes(searchText)) : shops),
    [shops, searchText]
  )
  const visibleShops = filteredShops.slice(0, filters.page * SHOPS_PER_PAGE)

  useEffect(() => {
    getShops({
      page: 1,
      pageSize: ALL_SHOPS_PAGE_SIZE,
      categories: filters.categories,
      towns: filters.towns,
      randomize: false,
    })
      .then((result) => {
        setShops(result.shops)
        setLoading(false)
      })
      .catch(() => console.log("Error loading shops"))
  }, [filters.categories, filters.towns])

  useEffect(() => {
    getDistricts().then((districts) => {
      setTowns(districts.map((d) => d.towns).flat())
    })
    getCategories().then((c) => setCategories(c))
  }, [])

  const loadMore = () => {
    setFilters({ ...filters, page: filters.page + 1 })
  }

  const setShopFilters = (towns: string[], categories: string[]) => {
    setFilters({ ...filters, towns, categories, page: 1 })
  }

  const setSearchFilter = (search: string) => {
    setFilters({ ...filters, search, page: 1 })
  }

  return (
    <>
      <div className="row mx-auto mb-2" style={{ maxWidth: "1440px" }}>
        <div className="d-flex flex-wrap align-items-center">
          <div className="ms-2 text-nowrap">
            {!loading && filteredShops.length > 0 ? (
              <h5 className="mb-0">{filteredShops.length} attivit&agrave;</h5>
            ) : null}
          </div>
          {/* On mobile the search box goes on its own full-width line below the count and the filter button */}
          <div className="order-last order-md-0 col-12 col-md-4 col-lg-3 ms-md-auto me-md-2 mt-3 mt-md-0 ps-2 ps-md-0">
            <input
              type="search"
              className="form-control"
              placeholder="Cerca attivit&agrave;"
              aria-label="Cerca attivit&agrave; per nome"
              value={filters.search}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
          </div>
          <div className="ms-auto ms-md-0 flex-shrink-0">
            {towns !== undefined && categories !== undefined ? (
              <ShopsFilter
                towns={towns}
                categories={categories}
                townsFilter={filters.towns}
                categoriesFilter={filters.categories}
                setFilters={setShopFilters}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className="row mx-auto" style={{ maxWidth: "1440px" }}>
        {visibleShops.length > 0 ? (
          visibleShops.map((shop) => (
            <div className="col-12 col-md-6 col-lg-4 col-xl-3 mt-3 mb-3" key={shop.id}>
              <ShopCard
                shopId={shop.id}
                shopImg={shop.photos}
                shopLogo={shop.company.logo}
                shopTitle={shop.name}
                shopDescription={shop.description}
                shopCategory={shop.category}
                shopLocation={shop.address}
                shopPhone={shop.phoneNumber}
                shopWebsite={shop.website}
                shopTown={shop.town}
              />
            </div>
          ))
        ) : !loading ? (
          <div className="col-12 text-center mt-4 mb-4">
            <h1>Nessun negozio trovato</h1>
            <p className="main-text">
              {filters.search
                ? "Prova a cambiare la ricerca o i filtri impostati"
                : "Prova a cambiare i filtri impostati"}
            </p>
          </div>
        ) : (
          <div className="col-12 text-center mt-4 mb-4">
            <h4>
              <img
                src={`${process.env.PUBLIC_URL}/img/icons/refresh.svg`}
                alt="Caricamento"
                className="image-loading me-2 mb-1"
              />
              Caricamento&hellip;
            </h4>
          </div>
        )}
      </div>
      {visibleShops.length < filteredShops.length ? (
        <div className="mx-auto text-center">
          <button className="btn btn-green-outline" onClick={loadMore}>
            Carica altri
          </button>
        </div>
      ) : null}
    </>
  )
}
